import {
	SortAscendingOutlined,
	SortDescendingOutlined,
} from "@ant-design/icons"
import { Table as AntdTable, type TableProps } from "antd"
import type { SortOrder } from "antd/es/table/interface"
import { Empty } from "components/Empty/Empty"
import { Indicator } from "components/Spinner/Spinner"
import { NoDataIcon } from "icons/NoDataIcon"
import { type ComponentProps, useMemo } from "react"
import { cn } from "styles/cn"

export type { ColumnType } from "antd/es/table"
export type { ExpandableConfig, SortOrder } from "antd/es/table/interface"

export const Table = <TData extends { id: string }>({
	columns = [],
	expandable,
	loading,
	pagination,
	rootClassName,
	...props
}: TableProps<TData>) => {
	const mappedColumns = useMemo(
		() =>
			columns.map((column) => ({
				...column,
				...(column.sorter && {
					sortIcon({ sortOrder }: { sortOrder: SortOrder | null }) {
						if (!sortOrder) {
							return null
						}
						return sortOrder === "ascend" ? (
							<SortAscendingOutlined aria-hidden />
						) : (
							<SortDescendingOutlined aria-hidden />
						)
					},
				}),
			})),
		[columns],
	)

	// The table announces its own load state so tests — and screen readers —
	// never have to reach for the spinner's markup.
	const isBusy =
		loading != null && typeof loading === "object"
			? !!loading.spinning
			: !!loading
	const components = useMemo(
		() => ({
			table: (props: ComponentProps<"table">) => (
				<table
					{...props}
					aria-busy={isBusy}
					aria-describedby={STATUS_ID}
					aria-label={`${isBusy ? "Updating " : ""}available items`}
				/>
			),
		}),
		[isBusy],
	)

	return (
		<AntdTable<TData>
			{...props}
			rootClassName={cn("isolate", rootClassName)}
			bordered
			size="small"
			columns={mappedColumns}
			locale={{
				emptyText: (
					<Empty
						Icon={NoDataIcon}
						title="No entries"
						className="justify-start"
					/>
				),
			}}
			scroll={{ x: "max-content", y: "fill" }}
			expandable={
				expandable && {
					columnTitle: <span className="sr-only">Details</span>,
					...expandable,
				}
			}
			loading={{
				...(loading != null && typeof loading === "object"
					? loading
					: { spinning: !!loading }),
				indicator: (
					<Indicator id={STATUS_ID} role="status" aria-live="polite" />
				),
			}}
			pagination={
				pagination === false
					? false
					: {
							pageSize: 20,
							...pagination,
							simple: true,
							defaultCurrent: 1,
							defaultPageSize: 20,
							showSizeChanger: false,
							placement: ["bottomCenter"],
							"aria-label": "Pagination",
							showTotal: (total, range) =>
								`${range[0]}-${range[1]} of ${total} items`,
						}
			}
			components={components}
			onHeaderRow={() => ({
				"aria-label": "Columns",
			})}
		/>
	)
}

const STATUS_ID = "table-status"
